import { useCallback, useEffect } from "react";
import { ScopeGroup, CreateScopeGroup, UpdateScopeGroup } from "../types/models/ScopeGroup.d";
import { UpdateEntityScopeGroup } from "../types/models/ScopeGroup";
import { useGetScopeGroupListQuery, useInsertScopeGroupMutation, useUpdateScopeGroupMutation, useDeleteScopeGroupMutation, } from "../apis/ScopeGroup.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useScopeGroup(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertScopeGroupMutation, { 
        isLoading: insertScopeGroupLoading, 
        isSuccess: insertScopeGroupIsSuccess,
        isError: insertScopeGroupIsError,
        error: insertScopeGroupError,
    }] = useInsertScopeGroupMutation()
    const [updateScopeGroupMutation, { 
        isLoading: updateScopeGroupLoading,
        isSuccess: updateScopeGroupIsSuccess,
        isError: updateScopeGroupIsError,
        error: updateScopeGroupError,
    }] = useUpdateScopeGroupMutation()
    const [deleteScopeGroupMutation, { 
        isLoading: deleteScopeGroupLoading,
        isSuccess: deleteScopeGroupIsSuccess,
        isError: deleteScopeGroupIsError,
        error: deleteScopeGroupError,
    }] = useDeleteScopeGroupMutation()

    const loading =
        insertScopeGroupLoading ||
        updateScopeGroupLoading ||
        deleteScopeGroupLoading ||
        false

    const insertScopeGroup = useCallback(async (scopeGroup: CreateScopeGroup, redirectPath: string|undefined) => {
        try {
            const data = await insertScopeGroupMutation(scopeGroup).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertScopeGroupMutation, navigate])
    
    useEffect(() => {
        if (insertScopeGroupIsSuccess) {
            openSuccessAlert('Scope Group created')
        } else if (insertScopeGroupIsError) {
            console.log("add error", insertScopeGroupError)
            openFailAlert(t`Scope Group add error: ` + insertScopeGroupError?.data?.message)
        }
    }, [
        insertScopeGroupIsSuccess,
        insertScopeGroupIsError,
        insertScopeGroupError
    ]);

    const updateScopeGroup = useCallback(async (scopeGroup: UpdateEntityScopeGroup, redirectPath: string|undefined) => {
        try {
            const data = await updateScopeGroupMutation(scopeGroup).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateScopeGroupMutation, navigate])
    
    useEffect(() => {
        if (updateScopeGroupIsSuccess) {
            openSuccessAlert(t`Scope Group updated`)
        } else if (updateScopeGroupIsError) {
            console.log("update error", updateScopeGroupError)
            openFailAlert(t`Scope Group update error: ` + updateScopeGroupError?.data?.message)
        }
    }, [
        updateScopeGroupIsSuccess,
        updateScopeGroupIsError,
        updateScopeGroupError
    ]);

    const deleteScopeGroup = useCallback((scopeGroup: ScopeGroup, redirectPath: string|undefined) => {
        openDialog(async () => {
            scopeGroup = {
                ...scopeGroup,
            }
            try {
                const { data } = await deleteScopeGroupMutation(scopeGroup);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteScopeGroupMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteScopeGroupIsSuccess) {
            openSuccessAlert(t`Scope Group deleted`)
        } else if (deleteScopeGroupIsError) {
            console.log("delete error", deleteScopeGroupError)
            openFailAlert(t`Scope Group delete error: ` + deleteScopeGroupError?.data?.message)
        }
    }, [
        deleteScopeGroupIsSuccess,
        deleteScopeGroupIsError,
        deleteScopeGroupError
    ]);

    return {
        loading,
        insertScopeGroup,
        updateScopeGroup,
        deleteScopeGroup
    }
}
